import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX } from "lucide-react";

/* ═══════════════════════════════════════════════════
   CINEMATIC PRODUCT DEMO V4 — Yestimate
   Full rebuild. Locked script. Scene-based structure.
   Stage 1: Structure + timing + placeholders.
   Stage 2: Real screen recordings replace placeholders.
   ═══════════════════════════════════════════════════ */

const FONT = {
  heading: "'Cabinet Grotesk', sans-serif",
  body: "'Manrope', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

/* Hide native browser video controls (Safari big play button, Chrome PiP, etc.) */
const VIDEO_SUPPRESS_CSS = `
  video::-webkit-media-controls-panel,
  video::-webkit-media-controls-play-button,
  video::-webkit-media-controls-start-playback-button,
  video::-webkit-media-controls { display: none !important; -webkit-appearance: none; }
  video::-moz-media-controls { display: none !important; }
  video::--webkit-media-controls-overlay-play-button { display: none !important; }
  @keyframes cursorClickRing {
    0% { width: 14px; height: 14px; opacity: 0.8; }
    100% { width: 36px; height: 36px; opacity: 0; }
  }
`;

// ─── ANIMATED CURSOR — Simulates mouse movement over UI screen recordings ───
// Each cursor path is an array of {x, y, at, click?} waypoints (% of video area)
// x/y are percentages (0-100). `at` = ms into the scene. `click` = show click ring.
const CURSOR_PATHS = {
  tech_intro: [
    { x: 50, y: 50, at: 400 },
    { x: 35, y: 40, at: 1200 },
    { x: 35, y: 55, at: 2000, click: true },
    { x: 45, y: 65, at: 3200 },
    { x: 55, y: 45, at: 4500, click: true },
  ],
  tech_evidence: [
    { x: 50, y: 40, at: 300 },
    { x: 60, y: 50, at: 1500, click: true },
    { x: 40, y: 60, at: 3000 },
    { x: 55, y: 55, at: 4500, click: true },
    { x: 50, y: 45, at: 6000 },
  ],
  tech_classify: [
    { x: 50, y: 50, at: 400 },
    { x: 40, y: 45, at: 1800 },
    { x: 55, y: 55, at: 3500, click: true },
    { x: 45, y: 50, at: 5500 },
    { x: 60, y: 45, at: 7500, click: true },
    { x: 50, y: 55, at: 9000 },
  ],
  tech_gate: [
    { x: 50, y: 45, at: 500 },
    { x: 40, y: 55, at: 2500, click: true },
    { x: 55, y: 40, at: 5000 },
    { x: 45, y: 60, at: 8000, click: true },
    { x: 50, y: 50, at: 11000 },
    { x: 55, y: 45, at: 14000, click: true },
  ],
  foh_job: [
    { x: 45, y: 40, at: 400 },
    { x: 55, y: 50, at: 2000, click: true },
    { x: 40, y: 55, at: 4000 },
    { x: 50, y: 45, at: 6500, click: true },
    { x: 55, y: 55, at: 8500 },
  ],
  foh_jce: [
    { x: 50, y: 45, at: 400 },
    { x: 40, y: 50, at: 2000, click: true },
    { x: 55, y: 55, at: 4000 },
    { x: 45, y: 45, at: 6000, click: true },
    { x: 50, y: 50, at: 8000 },
    { x: 55, y: 40, at: 10000, click: true },
  ],
  pricing_overview: [
    { x: 45, y: 45, at: 400 },
    { x: 55, y: 50, at: 2000, click: true },
    { x: 40, y: 55, at: 4000 },
    { x: 50, y: 45, at: 6000, click: true },
  ],
  pricing_intervals: [
    { x: 50, y: 45, at: 400 },
    { x: 45, y: 55, at: 2500, click: true },
    { x: 55, y: 50, at: 5000 },
    { x: 40, y: 45, at: 7500, click: true },
    { x: 50, y: 55, at: 9500 },
  ],
};

const CursorOverlay = ({ sceneId, t }) => {
  const path = CURSOR_PATHS[sceneId];
  if (!path || path.length === 0) return null;

  // Find current segment — interpolate between waypoints
  let currentWp = path[0];
  let nextWp = path[0];
  let segProgress = 0;

  for (let i = 0; i < path.length - 1; i++) {
    if (t >= path[i].at && t < path[i + 1].at) {
      currentWp = path[i];
      nextWp = path[i + 1];
      segProgress = (t - currentWp.at) / (nextWp.at - currentWp.at);
      break;
    }
    if (i === path.length - 2 && t >= path[i + 1].at) {
      currentWp = path[i + 1];
      nextWp = path[i + 1];
      segProgress = 1;
    }
  }

  // Ease function — smooth cursor movement
  const ease = (p) => p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
  const ep = ease(Math.min(1, segProgress));

  const x = currentWp.x + (nextWp.x - currentWp.x) * ep;
  const y = currentWp.y + (nextWp.y - currentWp.y) * ep;

  // Fade in cursor after scene start
  const opacity = t < path[0].at ? 0 : t < path[0].at + 300 ? (t - path[0].at) / 300 : 1;

  // Click ring — show briefly when arriving at a click waypoint
  const isNearClick = path.some(wp => wp.click && t >= wp.at && t < wp.at + 400);

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 15, opacity }}>
      {/* Cursor dot */}
      <div style={{
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        transform: "translate(-50%, -50%)",
        width: "14px",
        height: "14px",
        borderRadius: "50%",
        background: "rgba(255,255,255,0.9)",
        boxShadow: "0 0 8px rgba(0,0,0,0.4), 0 0 2px rgba(255,255,255,0.8)",
        transition: "width 0.15s, height 0.15s",
      }} />
      {/* Click ring */}
      {isNearClick && (
        <div style={{
          position: "absolute",
          left: `${x}%`,
          top: `${y}%`,
          transform: "translate(-50%, -50%)",
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          border: "2px solid rgba(255,255,255,0.6)",
          animation: "cursorClickRing 0.4s ease-out forwards",
        }} />
      )}
    </div>
  );
};


// ─── 19 SCENES — Locked Script ───
const SCENES = [
  // ═══ 1. OPENER ═══
  {
    id: "opener", duration: 7000, type: "opener",
    lines: [
      { text: "Every vehicle you inspect\u2026", at: 200, style: "hero" },
      { text: "has more work on it.", at: 1800, style: "hero" },
      { text: "The problem is\u2026", at: 3400, style: "dim" },
      { text: "you\u2019re not getting paid for it.", at: 4600, style: "drop" },
    ],
    ghostWords: [
      { text: "Not quoted", at: 4800, x: 15, y: 22 },
      { text: "Missed", at: 5100, x: 78, y: 35 },
      { text: "Not explained", at: 5400, x: 22, y: 72 },
      { text: "Lost revenue", at: 5600, x: 72, y: 68 },
    ],
    audio: "/demo-audio/v3/01_opener.mp3",
  },

  // ═══ 2. THE PROBLEM ═══
  {
    id: "problem", duration: 14000, type: "problem-sequence",
    bg: "#0A0A0B",
    // "Why?" brief flash, then straight into assets
    whyBeat: { text: "Why?", at: 0, holdUntil: 1200 },
    // Assets timed to narration
    assets: [
      { image: "/walkthrough/inconsistent_inspections.jpg", label: "Inconsistent inspections", at: 1200, duration: 1800, brightness: 1.1, overlay: 0.1, zoom: false },
      { image: "/walkthrough/vague_notes.jpg", label: "Vague notes", at: 3000, duration: 1800 },
      { image: "/walkthrough/poor_evidence.jpg", label: "Weak evidence", at: 4800, duration: 2200, brightness: 1.3, overlay: 0.05, zoom: false },
      { image: "/walkthrough/missed_parts.jpg", label: "Missed parts. Lost revenue.", at: 7000, duration: 2200, brightness: 1.1, overlay: 0.08, zoom: false },
    ],
    // Cards for final narration lines
    cards: [
      { text: "And the customer?", at: 9700, icon: "question" },
      { text: "They\u2019re left unsure\u2026 so they say no.", at: 11200, icon: "x" },
    ],
    audio: "/demo-audio/v3/02_problem.mp3",
  },

  // ═══ 3. TECH INTRO ═══
  {
    id: "tech_intro", duration: 6000, type: "video-scene",
    videoSrc: "/walkthrough/tech_workflow_4x.mp4",
    badge: "TECHNICIAN", badgeColor: "#3B82F6",
    caption: "Every inspection follows a structured, intuitive and guided process.",
    audio: "/demo-audio/v3/03_tech_intro.mp3",
  },

  // ═══ 4. EVIDENCE ═══
  {
    id: "tech_evidence", duration: 7200, type: "video-scene",
    videoSrc: "/walkthrough/evidence_scene.mp4",
    noLoop: true,
    badge: "EVIDENCE", badgeColor: "#3B82F6",
    caption: "Every input is mandatory. Captured with measurements. Backed up with photographic evidence.",
    audio: "/demo-audio/v3/04_tech_evidence.mp3",
  },

  // ═══ 5. CLASSIFICATION ═══
  {
    id: "tech_classify", duration: 10600, type: "video-scene",
    videoSrc: "/walkthrough/brake_pads_classify.mp4",
    noLoop: true,
    badge: "CLASSIFICATION", badgeColor: "#EF4444",
    caption: "Traffic light classification. Brake pads at 2.5mm \u2014 Red. Clear. Consistent. No interpretation.",
    audio: "/demo-audio/v3/05_tech_classify.mp3",
  },

  // ═══ 6. SAFETY GATE ═══
  {
    id: "tech_gate", duration: 16000, type: "video-scene",
    videoSrc: "/walkthrough/gate_scene.mp4",
    noLoop: true,
    badge: "SAFETY GATE", badgeColor: "#DC2626",
    caption: "Consistent, high-quality inspections \u2014 backed by evidence your customers trust.",
    audio: "/demo-audio/v3/06_tech_gate.mp3",
  },

  // ═══ 7. FOH TAKEOVER ═══
  {
    id: "foh_takeover", duration: 6000, type: "image-scene",
    imageSrc: "/walkthrough/foh_desk_render.jpg",
    badge: "FRONT OF HOUSE", badgeColor: "#8B5CF6",
    caption: "Front of house takes over \u2014 and nothing needs figuring out.",
    audio: "/demo-audio/v3/07_foh_takeover.mp3",
  },

  // ═══ 8. FOH JOB BUILD ═══
  {
    id: "foh_job", duration: 9600, type: "video-scene",
    videoSrc: "/walkthrough/scene_08_job_build.mp4",
    noLoop: true,
    badge: "JOB BUILD", badgeColor: "#8B5CF6",
    caption: "The system turns inspection findings into a complete, structured job.",
    audio: "/demo-audio/v3/08_foh_job.mp3",
  },

  // ═══ 9. JOB COMPLETENESS ENGINE ═══
  {
    id: "foh_jce", duration: 11400, type: "video-scene",
    videoSrc: "/walkthrough/scene_09_jce.mp4",
    noLoop: true,
    badge: "COMPLETENESS", badgeColor: "#8B5CF6",
    caption: "Both sides\u2026 tensioners\u2026 alignment\u2026 So nothing gets left behind.",
    audio: "/demo-audio/v3/09_foh_jce.mp3",
  },

  // ═══ 10. PRICING OVERVIEW ═══
  {
    id: "pricing_overview", duration: 8000, type: "video-scene",
    videoSrc: "/walkthrough/scene_10_pricing.mp4",
    noLoop: true,
    badge: "PRICING", badgeColor: "#10B981",
    caption: "Parts. Labour. Profit. No underquoting. No gaps. No missed revenue.",
    audio: "/demo-audio/v3/10_pricing_overview.mp3",
  },

  // ═══ 11. SERVICE INTERVALS ═══
  {
    id: "pricing_intervals", duration: 11000, type: "video-scene",
    videoSrc: "/walkthrough/scene_11_intervals.mp4",
    noLoop: true,
    badge: "INTERVALS", badgeColor: "#10B981",
    caption: "The estimate isn\u2019t guessed \u2014 it\u2019s built properly.",
    audio: "/demo-audio/v3/11_pricing_intervals.mp3",
  },

  // ═══ 12. CUSTOMER UNDERSTANDING ═══ (Pre-composited: homography-warped portal on phone)
  {
    id: "conversion", duration: 9000, type: "video-scene",
    videoSrc: "/walkthrough/portal-final-12.mp4?v=5",
    noLoop: true,
    badge: "CUSTOMER PORTAL", badgeColor: "#F59E0B",
    caption: "Because when the work is clear\u2026 the advice is consistent\u2026 customers don\u2019t hesitate.",
    audio: "/demo-audio/v3/12_conversion.mp3",
  },

  // ═══ 13. PORTAL OVERVIEW ═══ (Pre-composited)
  {
    id: "portal_open", duration: 8000, type: "video-scene",
    videoSrc: "/walkthrough/portal-final-13.mp4?v=5",
    noLoop: true,
    badge: "CUSTOMER PORTAL", badgeColor: "#F59E0B",
    caption: "Clear. Simple. Structured. Trusted.",
    audio: "/demo-audio/v3/13_portal_open.mp3",
  },

  // ═══ 14. RED & AMBER ═══ (Pre-composited)
  {
    id: "portal_redamber", duration: 8800, type: "video-scene",
    videoSrc: "/walkthrough/portal-final-14.mp4?v=5",
    noLoop: true,
    badge: "RED & AMBER", badgeColor: "#F59E0B",
    caption: "Red means act now. Amber highlights what\u2019s worth doing before it gets worse.",
    audio: "/demo-audio/v3/14_portal_redamber.mp3",
  },

  // ═══ 15. EXPLAIN & OVERCOME ═══ (Pre-composited)
  {
    id: "portal_explain", duration: 12200, type: "video-scene",
    videoSrc: "/walkthrough/portal-final-15.mp4?v=5",
    noLoop: true,
    badge: "EXPLAIN", badgeColor: "#F59E0B",
    caption: "So customers understand what they\u2019re paying for \u2014 and why it\u2019s worth doing.",
    audio: "/demo-audio/v3/15_portal_explain.mp3",
  },

  // ═══ 15b. CALL BRIDGE ═══
  {
    id: "call_bridge", duration: 3500, type: "text-reveal",
    bg: "#050505",
    lines: [
      { text: "And when customers want to talk it through\u2026", at: 200, style: "dim" },
    ],
    audio: "/demo-audio/v3/15b_call_bridge.mp3",
  },

  // ═══ 15c. CALL COMPANION ═══
  {
    id: "call_companion", duration: 7000, type: "call-companion",
    badge: "CALL COMPANION", badgeColor: "#3B82F6",
    caption: "The Call Companion guides every customer conversation\u2026 so nothing is missed, and every question is handled with confidence.",
    audio: "/demo-audio/v3/15c_call_companion.mp3",
    prompts: [
      { at: 300, type: "item", label: "Rear Brake Pads", detail: "3mm remaining", severity: "red" },
      { at: 1200, type: "guidance", label: "Explain safety risk", icon: "shield" },
      { at: 2300, type: "customer", label: "Customer asks:", detail: "Can this wait?" },
      { at: 3400, type: "response", label: "Below minimum. Recommend immediate replacement to maintain safe braking." },
    ],
  },

  // ═══ 16. APPROVAL ═══ (Pre-composited)
  {
    id: "portal_approve", duration: 14000, type: "video-scene",
    videoSrc: "/walkthrough/portal-final-16.mp4?v=5",
    noLoop: true,
    badge: "APPROVE", badgeColor: "#10B981",
    caption: "So whether they approve online\u2026 or on the call\u2026 they\u2019re confident to go ahead.",
    audio: "/demo-audio/v3/16_portal_approve_v2.mp3",
  },

  // ═══ 17. FOLLOW-UP ═══
  {
    id: "followup", duration: 9200, type: "followup-mockup",
    badge: "FOLLOW-UP", badgeColor: "#6366F1",
    caption: "Yestimate follows up, handles objections, and brings the work back.",
    audio: "/demo-audio/v3/17_followup.mp3",
  },

  // ═══ 18. PAYOFF ═══
  {
    id: "payoff", duration: 8000, type: "text-reveal",
    bg: "#050505",
    lines: [
      { text: "The work was always there.", at: 200, style: "hero" },
      { text: "Now it gets found properly\u2026", at: 2000, style: "hero" },
      { text: "built properly\u2026", at: 3400, style: "hero" },
      { text: "and sold properly.", at: 4600, style: "green" },
    ],
    audio: "/demo-audio/v3/18_payoff.mp3",
  },

  // ═══ 19. FINAL CTA ═══
  {
    id: "cta", duration: 10000, type: "cta",
    bg: "#050505",
    lines: [
      { text: "Nothing missed.", at: 200, style: "hero" },
      { text: "Nothing underquoted.", at: 1200, style: "hero" },
      { text: "No more lost work.", at: 2400, style: "red" },
      { text: "Start turning every job into more profit\u2026", at: 4200, style: "dim" },
      { text: "with Yestimate.", at: 5800, style: "brand" },
    ],
    audio: "/demo-audio/v3/19_cta.mp3",
  },
];

const TOTAL = SCENES.reduce((s, sc) => s + sc.duration, 0);

// ─── UTILITIES ───
function getSceneAt(elapsed) {
  let acc = 0;
  for (let i = 0; i < SCENES.length; i++) {
    if (elapsed < acc + SCENES[i].duration) return { idx: i, scene: SCENES[i], t: elapsed - acc };
    acc += SCENES[i].duration;
  }
  return { idx: SCENES.length - 1, scene: SCENES[SCENES.length - 1], t: SCENES[SCENES.length - 1].duration };
}

// ─── SCENE RENDERERS ───

// OPENER — High-impact first impression with texture, zoom, vignette, ghost words
const OpenerScene = ({ scene, t }) => {
  const zoomProgress = Math.min(t / scene.duration, 1);
  const scale = 1 + zoomProgress * 0.04; // subtle 1.0 → 1.04 zoom

  return (
    <div className="relative flex items-center justify-center h-full overflow-hidden" style={{ background: "#030303" }}>
      {/* Background texture layer — faint workshop document feel */}
      <div className="absolute inset-0" style={{
        transform: `scale(${scale})`,
        transition: "transform 0.3s linear",
      }}>
        {/* Noise grain texture */}
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
          opacity: 0.03,
          mixBlendMode: "overlay",
        }}/>
        {/* Faint workshop text watermarks — simulated job sheet feel */}
        <div className="absolute inset-0 flex flex-col justify-center items-center gap-3" style={{ opacity: 0.035, transform: "rotate(-2deg) scale(1.1)" }}>
          {["VEHICLE INSPECTION REPORT", "REG: ____  DATE: ____", "BRAKE PADS: __ mm   DISCS: __ mm", "TYRES FL: __  FR: __  RL: __  RR: __", "SUSPENSION: ________", "OIL LEVEL: ________", "ADDITIONAL NOTES: ________", "TECHNICIAN: ________  SIGNED: ________"].map((line, i) => (
            <span key={i} style={{ color: "#fff", fontSize: "14px", fontFamily: FONT.mono, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{line}</span>
          ))}
        </div>
      </div>

      {/* Vignette overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)",
      }}/>

      {/* Ghost words — appear faintly with the final line */}
      {scene.ghostWords?.map((gw, i) => {
        const visible = t >= gw.at;
        return (
          <div key={i} className="absolute pointer-events-none" style={{
            left: `${gw.x}%`, top: `${gw.y}%`,
            transform: "translate(-50%, -50%)",
            opacity: visible ? 0.08 : 0,
            transition: "opacity 1.2s ease",
            color: "#EF4444",
            fontSize: "clamp(0.7rem, 1.5vw, 1rem)",
            fontFamily: FONT.mono,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.2em",
          }}>{gw.text}</div>
        );
      })}

      {/* Main text content */}
      <div className="relative z-10 max-w-3xl px-8 text-center space-y-3" style={{ transform: `scale(${scale})`, transition: "transform 0.3s linear" }}>
        {scene.lines.map((line, i) => {
          const visible = t >= line.at;
          const isDrop = line.style === "drop";
          const styleMap = {
            hero: { color: "#FFFFFF", fontSize: "clamp(1.6rem, 4vw, 2.8rem)", fontWeight: 700, fontFamily: FONT.heading },
            dim: { color: "#666666", fontSize: "clamp(1.1rem, 2.8vw, 1.8rem)", fontWeight: 500, fontFamily: FONT.body },
            drop: { color: "#EF4444", fontSize: "clamp(1.5rem, 4vw, 2.6rem)", fontWeight: 800, fontFamily: FONT.heading },
          };
          return (
            <p key={i} style={{
              ...styleMap[line.style] || styleMap.hero,
              opacity: visible ? 1 : 0,
              transform: visible
                ? (isDrop ? "translateY(0) scale(1.04)" : "translateY(0)")
                : "translateY(16px)",
              transition: isDrop
                ? "opacity 0.5s ease, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)"
                : "opacity 0.6s ease, transform 0.6s ease",
              lineHeight: 1.3,
              textShadow: isDrop ? "0 0 30px rgba(239,68,68,0.3)" : "none",
            }}>{line.text}</p>
          );
        })}
      </div>
    </div>
  );
};

// TEXT REVEAL — Dramatic text on black
const TextRevealScene = ({ scene, t }) => {
  // Fade out the last 600ms of the scene for smooth transition
  const sceneEndFade = scene.duration ? Math.min(1, (scene.duration - t) / 600) : 1;
  const containerOpacity = Math.min(1, sceneEndFade);
  
  return (
    <div className="relative flex items-center justify-center h-full" style={{ background: scene.bg || "#050505" }}>
      <div className="max-w-3xl px-8 text-center space-y-3" style={{ opacity: containerOpacity, transition: "opacity 0.3s ease" }}>
        {scene.lines.map((line, i) => {
          const visible = t >= line.at;
          const styleMap = {
            hero: { color: "#FFFFFF", fontSize: "clamp(1.6rem, 4vw, 2.8rem)", fontWeight: 700, fontFamily: FONT.heading },
            dim: { color: "#888888", fontSize: "clamp(1.2rem, 3vw, 2rem)", fontWeight: 500, fontFamily: FONT.body },
            red: { color: "#EF4444", fontSize: "clamp(1.4rem, 3.5vw, 2.4rem)", fontWeight: 700, fontFamily: FONT.heading },
            green: { color: "#10B981", fontSize: "clamp(1.4rem, 3.5vw, 2.4rem)", fontWeight: 700, fontFamily: FONT.heading },
            brand: { color: "#10B981", fontSize: "clamp(1.8rem, 4.5vw, 3.2rem)", fontWeight: 800, fontFamily: FONT.heading },
          };
          return (
            <p key={i} style={{
              ...styleMap[line.style] || styleMap.hero,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
              lineHeight: 1.3,
            }}>{line.text}</p>
          );
        })}
      </div>
    </div>
  );
};

// PROBLEM SEQUENCE — Visual assets (hard cuts) then problem cards
const ProblemSequenceScene = ({ scene, t }) => {
  const iconMap = {
    warn: (<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2L18 17H2L10 2Z" stroke="#F59E0B" strokeWidth="1.5" fill="none"/><line x1="10" y1="8" x2="10" y2="12" stroke="#F59E0B" strokeWidth="1.5"/><circle cx="10" cy="14.5" r="0.8" fill="#F59E0B"/></svg>),
    cost: (<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#EF4444" strokeWidth="1.5"/><text x="10" y="14" textAnchor="middle" fill="#EF4444" fontSize="11" fontWeight="bold">{"\u00A3"}</text></svg>),
    question: (<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#888" strokeWidth="1.5"/><text x="10" y="14" textAnchor="middle" fill="#888" fontSize="12" fontWeight="bold">?</text></svg>),
    x: (<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#EF4444" strokeWidth="1.5"/><line x1="7" y1="7" x2="13" y2="13" stroke="#EF4444" strokeWidth="1.5"/><line x1="13" y1="7" x2="7" y2="13" stroke="#EF4444" strokeWidth="1.5"/></svg>),
  };

  // Determine which asset is active (hard cuts)
  const assets = scene.assets || [];
  const assetsEnd = assets.length > 0 ? assets[assets.length - 1].at + assets[assets.length - 1].duration : 0;
  const inAssetPhase = t < assetsEnd;

  // "Why?" beat — prominent on black before assets start
  const wb = scene.whyBeat;
  if (wb && t >= wb.at && t < wb.holdUntil) {
    const whyT = t - wb.at;
    const visible = whyT > 0;
    // Fade out towards the end of the holdUntil period for smooth transition to assets
    const fadeOut = Math.min(1, (wb.holdUntil - t) / 400);
    return (
      <div className="relative flex items-center justify-center h-full" style={{ background: "#050505" }}>
        <p style={{
          color: "#FFFFFF",
          fontSize: "clamp(3rem, 8vw, 6rem)",
          fontWeight: 900,
          fontFamily: FONT.heading,
          letterSpacing: "-0.02em",
          textShadow: "0 0 40px rgba(255,255,255,0.15)",
          opacity: visible ? fadeOut : 0,
          transform: visible ? "scale(1)" : "scale(0.85)",
          transition: "opacity 0.3s ease, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
        }}>{wb.text}</p>
      </div>
    );
  }

  // Asset phase: crossfade between images
  if (inAssetPhase) {
    const FADE = 600; // ms crossfade duration between assets

    return (
      <div className="relative h-full w-full overflow-hidden" style={{ background: "#080808" }}>
        {assets.map((asset, i) => {
          const assetT = t - asset.at;
          const noZoom = asset.zoom === false;
          const zoom = noZoom ? 1 : 1 + (Math.max(0, assetT) / asset.duration) * 0.03;

          // Fade in at start, fade out at end
          let opacity = 0;
          if (assetT >= 0 && assetT < asset.duration) {
            const fadeIn = Math.min(assetT / FADE, 1);
            const fadeOut = Math.min((asset.duration - assetT) / FADE, 1);
            opacity = Math.min(fadeIn, fadeOut);
          }

          const labelVisible = assetT > 500 && assetT < asset.duration - 200;

          return (
            <div key={i} className="absolute inset-0" style={{ opacity, transition: `opacity ${FADE}ms ease` }}>
              <div className="absolute inset-0" style={{
                backgroundImage: `url(${asset.image})`,
                backgroundSize: noZoom ? "contain" : "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                filter: `saturate(0.5) brightness(${asset.brightness || 0.6})`,
                transform: noZoom ? "none" : `scale(${zoom})`,
                transition: noZoom ? "none" : "transform 0.2s linear",
              }}/>
              <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${asset.overlay ?? 0.35})` }}/>
              <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)" }}/>
              <div className="absolute inset-0 flex items-center justify-center">
                <span style={{
                  color: "#fff",
                  fontSize: "clamp(1.4rem, 3.5vw, 2.2rem)",
                  fontWeight: 700,
                  fontFamily: FONT.heading,
                  letterSpacing: "0.02em",
                  textShadow: "0 2px 20px rgba(0,0,0,0.8)",
                  opacity: labelVisible ? 1 : 0,
                  transform: labelVisible ? "translateY(0)" : "translateY(10px)",
                  transition: "opacity 0.4s ease, transform 0.4s ease",
                }}>{asset.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Cards phase — prominent fade-in with zoom
  return (
    <div className="relative flex items-center justify-center h-full" style={{ background: "#0A0A0B" }}>
      <div className="max-w-2xl px-8 space-y-6 text-center">
        {scene.cards.map((card, i) => {
          const visible = t >= card.at;
          const isLast = card.icon === "x";
          return (
            <div key={i} style={{
              padding: "16px 28px", borderRadius: "12px",
              background: isLast ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.04)",
              border: isLast ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(255,255,255,0.08)",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0) scale(1)" : "translateY(30px) scale(0.9)",
              transition: "opacity 0.6s ease, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
            }}>
              <span style={{
                color: isLast ? "#EF4444" : "#eee",
                fontSize: isLast ? "clamp(1.2rem, 2.5vw, 1.6rem)" : "clamp(1.1rem, 2vw, 1.4rem)",
                fontFamily: FONT.heading,
                fontWeight: isLast ? 700 : 600,
                letterSpacing: "0.01em",
              }}>{card.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// IMAGE SCENE — Static image with cinematic subtle zoom
const ImageScene = ({ scene, t }) => {
  const progress = Math.min(t / scene.duration, 1);
  const scale = 1 + progress * 0.04; // subtle 1.0 → 1.04 zoom

  return (
    <div className="relative flex flex-col items-center justify-center h-full overflow-hidden" style={{ background: "#0A0A0A" }}>
      {/* Image with smooth zoom */}
      <div className="absolute inset-0" style={{
        backgroundImage: `url(${scene.imageSrc})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "brightness(0.85) saturate(0.9)",
        transform: `scale(${scale})`,
        transition: "transform 0.5s linear",
        willChange: "transform",
      }}/>
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)",
      }}/>
      {/* Bottom gradient for caption readability */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "linear-gradient(to top, rgba(0,0,0,.6) 0%, transparent 30%, transparent 80%, rgba(0,0,0,.3) 100%)",
      }}/>
      {/* Badge */}
      {scene.badge && (
        <div className="absolute top-5 left-6 z-20" style={{ opacity: t > 300 ? 1 : 0, transform: t > 300 ? "translateY(0)" : "translateY(-8px)", transition: "opacity .4s ease, transform .4s ease" }}>
          <span className="px-4 py-1.5 rounded text-xs font-bold uppercase tracking-[.15em] text-white"
            style={{ backgroundColor: scene.badgeColor || "#3B82F6", boxShadow: `0 0 20px ${scene.badgeColor || "#3B82F6"}66` }}
          >{scene.badge}</span>
        </div>
      )}
      {/* Caption */}
      {scene.caption && (
        <div className="absolute bottom-7 left-0 right-0 text-center z-20 px-8" style={{ opacity: t > 500 ? 1 : 0, transform: t > 500 ? "translateY(0)" : "translateY(8px)", transition: "opacity .5s ease, transform .5s ease" }}>
          <p className="text-base text-white font-semibold tracking-wide" style={{ fontFamily: FONT.body, textShadow: "0 2px 12px rgba(0,0,0,.9), 0 0 40px rgba(0,0,0,.6)" }}>{scene.caption}</p>
        </div>
      )}
    </div>
  );
};

// VIDEO SCENE — Plays a real screen recording (including pre-composited phone scenes)
const VideoScene = ({ scene, t }) => {
  const videoRef = useRef(null);
  const isCinematic = scene.videoSrc?.includes('composite-') || scene.videoSrc?.includes('portal-v4-') || scene.videoSrc?.includes('portal-final-');
  
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      const tryPlay = () => { 
        const p = v.play(); 
        if (p) p.catch(() => {}); 
      };
      if (v.readyState >= 3) {
        tryPlay();
      } else if (v.readyState >= 1) {
        tryPlay();
      } else {
        v.addEventListener('loadedmetadata', tryPlay, { once: true });
        v.load();
      }
    }
  }, [scene.id]);
  
  useEffect(() => {
    const v = videoRef.current;
    if (v && v.paused && t > 100) { const p = v.play(); if (p) p.catch(() => {}); }
  }, [t]);

  return (
    <div className="relative flex flex-col items-center justify-center h-full overflow-hidden" style={{ background: "linear-gradient(135deg,#0A0A0A 0%,#111118 100%)" }}>
      <div className="absolute inset-4 flex items-center justify-center">
        {/* Video container with click-blocking overlay */}
        <div style={{ position: "relative", display: "inline-flex", maxWidth: isCinematic ? "100%" : "90%", maxHeight: isCinematic ? "95%" : "85%" }}>
          <video
            key={scene.id}
            ref={videoRef}
            muted playsInline autoPlay preload="auto"
            loop={!scene.noLoop}
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            className={isCinematic ? "" : "rounded-lg shadow-2xl"}
            style={{
              width: "100%", height: "100%",
              objectFit: "contain",
              border: isCinematic ? "none" : "1px solid rgba(255,255,255,.08)",
            }}
          >
            <source src={scene.videoSrc} type="video/mp4"/>
          </video>
          {/* Invisible overlay — blocks ALL browser-native video controls */}
          <div style={{ position: "absolute", inset: 0, zIndex: 10 }} />
          {/* Animated cursor overlay — UI screen recordings only */}
          {!isCinematic && <CursorOverlay sceneId={scene.id} t={t} />}
        </div>
      </div>
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 20%, transparent 80%, rgba(0,0,0,.25) 100%)" }}/>
      {/* Badge */}
      {scene.badge && (
        <div className="absolute top-5 left-6 z-20" style={{ opacity: t > 300 ? 1 : 0, transform: t > 300 ? "translateY(0)" : "translateY(-8px)", transition: "opacity .4s ease, transform .4s ease" }}>
          <span className="px-4 py-1.5 rounded text-xs font-bold uppercase tracking-[.15em] text-white"
            style={{ backgroundColor: scene.badgeColor || "#3B82F6", boxShadow: `0 0 20px ${scene.badgeColor || "#3B82F6"}66` }}
          >{scene.badge}</span>
        </div>
      )}
      {/* Caption */}
      {scene.caption && (
        <div className="absolute bottom-7 left-0 right-0 text-center z-20 px-8" style={{ opacity: t > 500 ? 1 : 0, transform: t > 500 ? "translateY(0)" : "translateY(8px)", transition: "opacity .5s ease, transform .5s ease" }}>
          <p className="text-base text-white font-semibold tracking-wide" style={{ fontFamily: FONT.body, textShadow: "0 2px 12px rgba(0,0,0,.9), 0 0 40px rgba(0,0,0,.6)" }}>{scene.caption}</p>
        </div>
      )}
    </div>
  );
};

// PLACEHOLDER SCENE — Mockup UI frame for Stage 2 replacement

// CALL COMPANION SCENE — Advisor on phone with staggered UI guidance prompts
const CallCompanionScene = ({ scene, t }) => {
  const show = (delay) => t > delay;
  const fadeIn = (delay) => ({
    opacity: show(delay) ? 1 : 0,
    transform: show(delay) ? "translateY(0)" : "translateY(8px)",
    transition: "opacity 0.45s ease, transform 0.45s ease",
  });

  const prompts = scene.prompts || [];

  const severityColor = { red: "#EF4444", amber: "#F59E0B", green: "#10B981" };

  return (
    <div className="relative flex flex-col items-center justify-center h-full overflow-hidden" style={{ background: "linear-gradient(135deg, #08080A 0%, #0D0D14 100%)" }}>
      {/* Badge */}
      {scene.badge && (
        <div className="absolute top-4 left-6 z-20" style={fadeIn(200)}>
          <span className="px-3 py-1 rounded text-[11px] font-bold uppercase tracking-[.15em] text-white"
            style={{ backgroundColor: scene.badgeColor, boxShadow: `0 0 12px ${scene.badgeColor}44` }}
          >{scene.badge}</span>
        </div>
      )}

      {/* Main content — split layout */}
      <div style={{ display: "flex", gap: "28px", width: "min(90%, 820px)", alignItems: "center", ...fadeIn(250) }}>

        {/* Left: Advisor call visual */}
        <div style={{ flex: "0 0 200px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", ...fadeIn(200) }}>
          {/* Phone circle with pulse */}
          <div style={{ position: "relative", width: "88px", height: "88px" }}>
            {/* Pulse rings */}
            <div style={{
              position: "absolute", inset: "-12px", borderRadius: "50%",
              border: "1.5px solid rgba(59,130,246,0.15)",
              animation: show(300) ? "pulse 2s ease-in-out infinite" : "none",
              opacity: show(300) ? 1 : 0, transition: "opacity 0.5s ease",
            }}/>
            <div style={{
              position: "absolute", inset: "-24px", borderRadius: "50%",
              border: "1px solid rgba(59,130,246,0.08)",
              animation: show(300) ? "pulse 2s ease-in-out infinite 0.5s" : "none",
              opacity: show(300) ? 1 : 0, transition: "opacity 0.5s ease",
            }}/>
            {/* Icon circle */}
            <div style={{
              width: "88px", height: "88px", borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(59,130,246,0.05) 100%)",
              border: "1.5px solid rgba(59,130,246,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
            </div>
          </div>
          {/* Advisor label */}
          <div style={{ textAlign: "center", ...fadeIn(400) }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "rgba(255,255,255,0.7)", fontFamily: FONT.body, letterSpacing: "0.02em" }}>Service Advisor</div>
            <div style={{ fontSize: "9px", color: "rgba(59,130,246,0.6)", fontFamily: FONT.mono, marginTop: "2px" }}>
              {show(400) ? "Call in progress" : ""}
            </div>
          </div>
          {/* Call timer */}
          <div style={{
            padding: "3px 10px", borderRadius: "10px",
            background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.15)",
            fontSize: "10px", fontFamily: FONT.mono, color: "rgba(59,130,246,0.7)",
            ...fadeIn(500),
          }}>02:47</div>
        </div>

        {/* Right: Staggered prompt cards */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
          {prompts.map((p, i) => {
            const visible = show(p.at);
            const cardStyle = {
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(16px)",
              transition: "opacity 0.4s ease, transform 0.4s ease",
            };

            if (p.type === "item") {
              return (
                <div key={i} style={{
                  ...cardStyle,
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 14px", borderRadius: "10px",
                  background: `${severityColor[p.severity] || "#EF4444"}08`,
                  border: `1px solid ${severityColor[p.severity] || "#EF4444"}20`,
                }}>
                  <div style={{
                    width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                    background: severityColor[p.severity] || "#EF4444",
                    boxShadow: `0 0 8px ${severityColor[p.severity] || "#EF4444"}66`,
                  }}/>
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: severityColor[p.severity] || "#EF4444", fontFamily: FONT.body }}>{p.label}</div>
                    <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", fontFamily: FONT.mono }}>{p.detail}</div>
                  </div>
                </div>
              );
            }
            if (p.type === "guidance") {
              return (
                <div key={i} style={{
                  ...cardStyle,
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 14px", borderRadius: "10px",
                  background: "rgba(59,130,246,0.06)",
                  border: "1px solid rgba(59,130,246,0.15)",
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "rgba(59,130,246,0.9)", fontFamily: FONT.body }}>{p.label}</div>
                </div>
              );
            }
            if (p.type === "customer") {
              return (
                <div key={i} style={{
                  ...cardStyle,
                  padding: "10px 14px", borderRadius: "10px",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderLeft: "3px solid rgba(255,255,255,0.15)",
                }}>
                  <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.35)", fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>{p.label}</div>
                  <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", fontFamily: FONT.body, fontStyle: "italic" }}>{`"${p.detail}"`}</div>
                </div>
              );
            }
            if (p.type === "response") {
              return (
                <div key={i} style={{
                  ...cardStyle,
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "10px 14px", borderRadius: "10px",
                  background: "rgba(16,185,129,0.06)",
                  border: "1px solid rgba(16,185,129,0.15)",
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: "2px", flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <div style={{ fontSize: "11px", color: "rgba(16,185,129,0.85)", fontFamily: FONT.body, lineHeight: 1.4 }}>{p.label}</div>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>

      {/* Pulse keyframes */}
      <style dangerouslySetInnerHTML={{ __html: `@keyframes pulse { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.08); opacity: 1; } }` }} />

      {/* Caption */}
      {scene.caption && (
        <div className="absolute bottom-7 left-0 right-0 text-center z-20 px-8" style={{ opacity: t > 500 ? 1 : 0, transform: t > 500 ? "translateY(0)" : "translateY(8px)", transition: "opacity .5s ease, transform .5s ease" }}>
          <p className="text-base text-white font-semibold tracking-wide" style={{ fontFamily: FONT.body, textShadow: "0 2px 12px rgba(0,0,0,.9), 0 0 40px rgba(0,0,0,.6)" }}>{scene.caption}</p>
        </div>
      )}
    </div>
  );
};

// FOLLOW-UP MOCKUP SCENE — Rich dashboard showing declined work recovery pipeline
const FollowUpMockupScene = ({ scene, t }) => {
  // Staggered animation timing
  const show = (delay) => t > delay;
  const fadeIn = (delay) => ({
    opacity: show(delay) ? 1 : 0,
    transform: show(delay) ? "translateY(0)" : "translateY(6px)",
    transition: "opacity 0.5s ease, transform 0.5s ease",
  });

  // Mock data for the follow-up pipeline
  const stats = [
    { label: "Declined Value", value: "\u00A33,847", color: "#EF4444", icon: "\u00A3" },
    { label: "In Follow-Up", value: "12", color: "#F59E0B", icon: "\u21BB" },
    { label: "Recovered MTD", value: "\u00A32,190", color: "#10B981", icon: "\u2713" },
    { label: "Recovery Rate", value: "57%", color: "#6366F1", icon: "%" },
  ];

  const chaseItems = [
    { reg: "AB21 XYZ", customer: "J. Mitchell", item: "Front Brake Discs & Pads", value: "\u00A3487.60", status: "chase_due", statusLabel: "Chase Due Today", statusColor: "#EF4444", daysSince: 3, lastAction: "SMS sent 3 days ago", nextAction: "Call scheduled 2:30pm" },
    { reg: "KF19 PLR", customer: "S. Thompson", item: "Timing Belt Kit", value: "\u00A3762.00", status: "awaiting", statusLabel: "Awaiting Response", statusColor: "#F59E0B", daysSince: 5, lastAction: "Explanation email sent", nextAction: "Auto follow-up in 2 days" },
    { reg: "DG22 MNP", customer: "R. Patel", item: "Suspension Arm (N/S)", value: "\u00A3389.00", status: "re_engaged", statusLabel: "Customer Re-engaged", statusColor: "#10B981", daysSince: 7, lastAction: "Customer called back", nextAction: "Booking confirmed Wed" },
    { reg: "HV20 TRS", customer: "L. Davies", item: "Battery Replacement (AGM)", value: "\u00A3312.40", status: "objection", statusLabel: "Price Objection", statusColor: "#F97316", daysSince: 4, lastAction: "Declined - too expensive", nextAction: "Value explanation queued" },
    { reg: "WN18 CFG", customer: "M. Wilson", item: "AC Regas + Pollen Filter", value: "\u00A3189.00", status: "recovered", statusLabel: "Recovered", statusColor: "#10B981", daysSince: 10, lastAction: "Booked & completed", nextAction: "\u2014" },
    { reg: "YE71 ABC", customer: "T. Hughes", item: "Rear Brake Pads + Fitting", value: "\u00A3267.80", status: "chase_due", statusLabel: "2nd Chase Due", statusColor: "#EF4444", daysSince: 8, lastAction: "1st chase - no response", nextAction: "2nd SMS with urgency" },
  ];

  const timeline = [
    { day: "Day 0", action: "Customer declines item", auto: true },
    { day: "Day 3", action: "Automated SMS reminder", auto: true },
    { day: "Day 5", action: "Explanation email with evidence", auto: true },
    { day: "Day 7", action: "FOH call task created", auto: false },
    { day: "Day 14", action: "Final follow-up with urgency", auto: true },
  ];

  return (
    <div className="relative flex flex-col items-center justify-center h-full overflow-hidden" style={{ background: "linear-gradient(135deg,#0A0A0B 0%,#0F0F14 100%)" }}>
      {/* Badge */}
      {scene.badge && (
        <div className="absolute top-4 left-6 z-20" style={fadeIn(200)}>
          <span className="px-3 py-1 rounded text-[11px] font-bold uppercase tracking-[.15em] text-white"
            style={{ backgroundColor: scene.badgeColor, boxShadow: `0 0 12px ${scene.badgeColor}44` }}
          >{scene.badge}</span>
        </div>
      )}

      {/* Main Frame */}
      <div style={{
        width: "min(92%, 880px)", height: "min(82%, 520px)",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.02)",
        backdropFilter: "blur(8px)",
        overflow: "hidden",
        ...fadeIn(250),
        display: "flex", flexDirection: "column",
      }}>
        {/* Browser Chrome */}
        <div style={{ height: "34px", background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", padding: "0 12px", gap: "6px", flexShrink: 0 }}>
          <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#EF4444" }}/>
          <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#F59E0B" }}/>
          <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#10B981" }}/>
          <div style={{ flex: 1, marginLeft: "10px", height: "18px", borderRadius: "5px", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.25)", fontFamily: FONT.mono }}>app.yestimate.co.uk/follow-up</span>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left: Stats + Chase Pipeline */}
          <div style={{ flex: 1, padding: "14px 16px", display: "flex", flexDirection: "column", gap: "10px", overflow: "hidden" }}>
            {/* Stats Row */}
            <div style={{ display: "flex", gap: "8px", ...fadeIn(400) }}>
              {stats.map((s, i) => (
                <div key={i} style={{
                  flex: 1, padding: "8px 10px", borderRadius: "8px",
                  background: `${s.color}10`, border: `1px solid ${s.color}25`,
                  ...fadeIn(500 + i * 200),
                }}>
                  <div style={{ fontSize: "8px", color: "rgba(255,255,255,0.4)", fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>{s.label}</div>
                  <div style={{ fontSize: "16px", fontWeight: 800, color: s.color, fontFamily: FONT.heading }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Section Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", ...fadeIn(1200) }}>
              <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.15em" }}>Active Chase Pipeline</span>
              <div style={{ display: "flex", gap: "4px" }}>
                <span style={{ fontSize: "8px", padding: "2px 6px", borderRadius: "4px", background: "rgba(239,68,68,0.15)", color: "#EF4444", fontWeight: 600 }}>3 Due</span>
                <span style={{ fontSize: "8px", padding: "2px 6px", borderRadius: "4px", background: "rgba(16,185,129,0.15)", color: "#10B981", fontWeight: 600 }}>2 Recovered</span>
              </div>
            </div>

            {/* Chase Items Table */}
            <div style={{ flex: 1, overflow: "hidden", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.06)" }}>
              {/* Header */}
              <div style={{ display: "flex", padding: "6px 10px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid rgba(255,255,255,0.06)", ...fadeIn(1400) }}>
                {["Vehicle", "Customer", "Declined Item", "Value", "Status", "Next Action"].map((h, i) => (
                  <div key={i} style={{ flex: i === 2 ? 1.4 : i === 5 ? 1.2 : 0.7, fontSize: "8px", color: "rgba(255,255,255,0.35)", fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</div>
                ))}
              </div>
              {/* Rows */}
              {chaseItems.map((item, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", padding: "7px 10px",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  background: item.status === "recovered" ? "rgba(16,185,129,0.04)" : item.status === "chase_due" ? "rgba(239,68,68,0.03)" : "transparent",
                  ...fadeIn(1600 + i * 300),
                }}>
                  <div style={{ flex: 0.7, fontSize: "9px", color: "#FFFFFF", fontFamily: FONT.mono, fontWeight: 600 }}>{item.reg}</div>
                  <div style={{ flex: 0.7, fontSize: "9px", color: "rgba(255,255,255,0.7)" }}>{item.customer}</div>
                  <div style={{ flex: 1.4, fontSize: "9px", color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>{item.item}</div>
                  <div style={{ flex: 0.7, fontSize: "9px", color: "#FFFFFF", fontWeight: 700 }}>{item.value}</div>
                  <div style={{ flex: 0.7 }}>
                    <span style={{
                      fontSize: "7px", padding: "2px 5px", borderRadius: "3px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                      background: `${item.statusColor}20`, color: item.statusColor, whiteSpace: "nowrap",
                    }}>{item.statusLabel}</span>
                  </div>
                  <div style={{ flex: 1.2, fontSize: "8px", color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}>{item.nextAction}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Follow-Up Timeline */}
          <div style={{ width: "200px", borderLeft: "1px solid rgba(255,255,255,0.06)", padding: "14px", display: "flex", flexDirection: "column", gap: "8px", ...fadeIn(2400) }}>
            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)", fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "4px" }}>Auto Chase Timeline</div>
            
            {timeline.map((step, i) => (
              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start", ...fadeIn(2600 + i * 400) }}>
                {/* Timeline dot + line */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "12px", flexShrink: 0 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: step.auto ? "#6366F1" : "#F59E0B",
                    boxShadow: step.auto ? "0 0 6px #6366F166" : "0 0 6px #F59E0B66",
                    flexShrink: 0,
                  }}/>
                  {i < timeline.length - 1 && (
                    <div style={{ width: "1px", flex: 1, minHeight: "16px", background: "rgba(255,255,255,0.08)" }}/>
                  )}
                </div>
                <div style={{ flex: 1, paddingBottom: "6px" }}>
                  <div style={{ fontSize: "8px", fontWeight: 700, color: "rgba(255,255,255,0.6)", fontFamily: FONT.mono }}>{step.day}</div>
                  <div style={{ fontSize: "8px", color: "rgba(255,255,255,0.45)", lineHeight: 1.3, marginTop: "1px" }}>{step.action}</div>
                  {step.auto && (
                    <span style={{ fontSize: "7px", padding: "1px 4px", borderRadius: "2px", background: "rgba(99,102,241,0.15)", color: "#818CF8", fontWeight: 600, marginTop: "2px", display: "inline-block" }}>AUTO</span>
                  )}
                </div>
              </div>
            ))}

            {/* Recovery Summary */}
            <div style={{ marginTop: "auto", padding: "8px", borderRadius: "6px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", ...fadeIn(4800) }}>
              <div style={{ fontSize: "7px", color: "#10B981", fontFamily: FONT.mono, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "4px" }}>This Month</div>
              <div style={{ fontSize: "14px", fontWeight: 800, color: "#10B981", fontFamily: FONT.heading }}>\u00A32,190</div>
              <div style={{ fontSize: "8px", color: "rgba(16,185,129,0.7)", marginTop: "2px" }}>recovered from declined work</div>
            </div>
          </div>
        </div>
      </div>

      {/* Caption */}
      {scene.caption && (
        <div className="absolute bottom-6 left-0 right-0 text-center z-20 px-8" style={{ opacity: t > 500 ? 1 : 0, transition: "opacity .5s ease" }}>
          <p className="text-sm text-white/85 font-medium tracking-wide" style={{ fontFamily: FONT.body, textShadow: "0 2px 8px rgba(0,0,0,.8)" }}>{scene.caption}</p>
        </div>
      )}
    </div>
  );
};

const PlaceholderScene = ({ scene, t }) => {
  const isPhone = scene.isPhone;
  const frameW = isPhone ? "280px" : "min(85%, 820px)";
  const frameH = isPhone ? "520px" : "min(75%, 480px)";

  return (
    <div className="relative flex flex-col items-center justify-center h-full overflow-hidden" style={{ background: "linear-gradient(135deg,#0A0A0B 0%,#0F0F14 100%)" }}>
      {/* Badge */}
      {scene.badge && (
        <div className="absolute top-4 left-6 z-20" style={{ opacity: t > 200 ? 1 : 0, transition: "opacity .4s ease" }}>
          <span className="px-3 py-1 rounded text-[11px] font-bold uppercase tracking-[.15em] text-white"
            style={{ backgroundColor: scene.badgeColor, boxShadow: `0 0 12px ${scene.badgeColor}44` }}
          >{scene.badge}</span>
        </div>
      )}

      {/* Mock Frame */}
      <div style={{
        width: frameW, height: frameH,
        borderRadius: isPhone ? "28px" : "12px",
        border: `1px solid rgba(255,255,255,0.08)`,
        background: "rgba(255,255,255,0.02)",
        backdropFilter: "blur(8px)",
        overflow: "hidden",
        opacity: t > 300 ? 1 : 0,
        transform: t > 300 ? "translateY(0) scale(1)" : "translateY(12px) scale(0.98)",
        transition: "opacity .6s ease, transform .6s ease",
        display: "flex", flexDirection: "column",
      }}>
        {/* Browser Chrome / Phone Notch */}
        {!isPhone ? (
          <div style={{ height: "36px", background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", padding: "0 14px", gap: "6px", flexShrink: 0 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444" }}/>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B" }}/>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#10B981" }}/>
            <div style={{ flex: 1, marginLeft: "12px", height: "20px", borderRadius: "6px", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.25)", fontFamily: FONT.mono }}>app.yestimate.co.uk</span>
            </div>
          </div>
        ) : (
          <div style={{ height: "32px", display: "flex", justifyContent: "center", alignItems: "flex-end", paddingBottom: "4px", flexShrink: 0 }}>
            <div style={{ width: "80px", height: "5px", borderRadius: "3px", background: "rgba(255,255,255,0.15)" }}/>
          </div>
        )}

        {/* Content Area */}
        <div style={{ flex: 1, padding: isPhone ? "16px" : "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {/* Title */}
          <div style={{
            fontSize: "11px", fontFamily: FONT.mono, color: "rgba(255,255,255,0.3)",
            textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: "4px",
            opacity: t > 600 ? 1 : 0, transition: "opacity .4s ease",
          }}>
            {scene.placeholderLabel}
          </div>

          {/* Placeholder Sections */}
          {scene.placeholderSections?.map((label, i) => {
            const sectionVisible = t > 800 + i * 400;
            return (
              <div key={i} style={{
                flex: 1, borderRadius: "8px",
                border: "1px dashed rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.015)",
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: sectionVisible ? 1 : 0,
                transform: sectionVisible ? "translateY(0)" : "translateY(8px)",
                transition: "opacity .4s ease, transform .4s ease",
              }}>
                <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.2)", fontFamily: FONT.body }}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Caption */}
      {scene.caption && (
        <div className="absolute bottom-6 left-0 right-0 text-center z-20 px-8" style={{ opacity: t > 500 ? 1 : 0, transition: "opacity .5s ease" }}>
          <p className="text-sm text-white/85 font-medium tracking-wide" style={{ fontFamily: FONT.body, textShadow: "0 2px 8px rgba(0,0,0,.8)" }}>{scene.caption}</p>
        </div>
      )}
    </div>
  );
};

// CTA SCENE — Final branding moment
const CTAScene = ({ scene, t }) => (
  <div className="relative flex items-center justify-center h-full" style={{ background: "#050505" }}>
    <div className="max-w-3xl px-8 text-center space-y-4">
      {scene.lines.map((line, i) => {
        const visible = t >= line.at;
        const styleMap = {
          hero: { color: "#FFFFFF", fontSize: "clamp(1.5rem, 3.5vw, 2.4rem)", fontWeight: 700, fontFamily: FONT.heading },
          dim: { color: "#888888", fontSize: "clamp(1.1rem, 2.5vw, 1.8rem)", fontWeight: 500, fontFamily: FONT.body },
          red: { color: "#EF4444", fontSize: "clamp(1.3rem, 3vw, 2.2rem)", fontWeight: 700, fontFamily: FONT.heading },
          brand: { color: "#10B981", fontSize: "clamp(2rem, 5vw, 3.6rem)", fontWeight: 800, fontFamily: FONT.heading, letterSpacing: "-0.02em" },
        };
        return (
          <p key={i} style={{
            ...styleMap[line.style] || styleMap.hero,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.96)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
            lineHeight: 1.2,
          }}>{line.text}</p>
        );
      })}
    </div>
  </div>
);

// ─── SCENE ROUTER ───
const SceneRenderer = ({ scene, t }) => {
  switch (scene.type) {
    case "opener": return <OpenerScene scene={scene} t={t} />;
    case "text-reveal": return <TextRevealScene scene={scene} t={t} />;
    case "problem-sequence": return <ProblemSequenceScene scene={scene} t={t} />;
    case "video-scene": return <VideoScene scene={scene} t={t} />;
    case "image-scene": return <ImageScene scene={scene} t={t} />;
    case "followup-mockup": return <FollowUpMockupScene scene={scene} t={t} />;
    case "call-companion": return <CallCompanionScene scene={scene} t={t} />;
    case "placeholder": return <PlaceholderScene scene={scene} t={t} />;
    case "cta": return <CTAScene scene={scene} t={t} />;
    default: return <TextRevealScene scene={scene} t={t} />;
  }
};

// ─── MAIN DEMO COMPONENT ───
export const CinematicDemo = ({ onClose }) => {
  const [playing, setPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const lastSceneRef = useRef(-1);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const pausedAtRef = useRef(0);
  const audioPreloadRef = useRef({});
  const videoPreloadRef = useRef({});

  // Crossfade duration (ms) — longer = smoother transitions between scenes
  const XFADE = 700;

  // Animation loop
  const tick = useCallback(() => {
    if (!startRef.current) startRef.current = performance.now();
    const now = performance.now();
    const el = pausedAtRef.current + (now - startRef.current);
    if (el >= TOTAL) {
      setElapsed(TOTAL);
      setPlaying(false);
      return;
    }
    setElapsed(el);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (playing) {
      startRef.current = performance.now();
      rafRef.current = requestAnimationFrame(tick);
    } else {
      pausedAtRef.current = elapsed;
      startRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, tick]);

  // Audio sync with pre-loading
  const { idx, scene, t } = getSceneAt(elapsed);

  // Pre-load ALL audio on mount (small files) + images
  useEffect(() => {
    SCENES.forEach(s => {
      if (s.audio && !audioPreloadRef.current[s.audio]) {
        const a = new Audio(s.audio);
        a.preload = "auto";
        a.load();
        audioPreloadRef.current[s.audio] = a;
      }
      // Pre-load images (small, safe to batch)
      if (s.imageSrc) { const img = new Image(); img.src = s.imageSrc; }
      if (s.assets) s.assets.forEach(a => { const img = new Image(); img.src = a.image; });
    });
  }, []);

  // Progressive video pre-loading: stay 5 scenes ahead for zero-lag transitions
  useEffect(() => {
    for (let i = idx; i < Math.min(idx + 5, SCENES.length); i++) {
      const src = SCENES[i].videoSrc;
      if (src && !videoPreloadRef.current[src]) {
        const v = document.createElement('video');
        v.preload = 'auto';
        v.muted = true;
        v.playsInline = true;
        v.src = src;
        v.load();
        videoPreloadRef.current[src] = v;
      }
    }
  }, [idx]);

  useEffect(() => {
    if (idx !== lastSceneRef.current) {
      lastSceneRef.current = idx;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (scene.audio && playing) {
        // Use pre-loaded audio if available
        const preloaded = audioPreloadRef.current[scene.audio];
        let a;
        if (preloaded) {
          a = preloaded;
          a.currentTime = 0;
          delete audioPreloadRef.current[scene.audio];
        } else {
          a = new Audio(scene.audio);
        }
        a.muted = muted;
        a.play().catch(() => {});
        audioRef.current = a;
      }
    }
  }, [idx, playing, scene, muted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (!playing && audioRef.current) audioRef.current.pause();
    if (playing && audioRef.current && audioRef.current.paused) audioRef.current.play().catch(() => {});
  }, [playing]);

  // Cleanup
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    Object.values(audioPreloadRef.current).forEach(a => { try { a.pause(); } catch(_){} });
    Object.values(videoPreloadRef.current).forEach(v => { try { v.pause(); v.src = ''; v.load(); } catch(_){} });
  }, []);

  const restart = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    lastSceneRef.current = -1;
    pausedAtRef.current = 0;
    startRef.current = null;
    setElapsed(0);
    setPlaying(true);
  };

  const togglePlay = () => setPlaying(p => !p);
  const progress = Math.min(elapsed / TOTAL, 1);
  const timeStr = `${Math.floor(elapsed / 1000)}s / ${Math.floor(TOTAL / 1000)}s`;

  // Click on progress bar to seek
  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const target = pct * TOTAL;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    lastSceneRef.current = -1;
    pausedAtRef.current = target;
    startRef.current = null;
    setElapsed(target);
    if (!playing) setPlaying(true);
  };

  // Crossfade: smooth ease-out during first XFADE ms of each scene
  // easeOut reveals content quickly then settles — avoids prolonged black
  const rawFade = Math.min(1, t / XFADE);
  const fadeInOpacity = 1 - (1 - rawFade) * (1 - rawFade); // easeOut — fast start, gentle end

  // Get previous scene for crossfade (only during transition overlap)
  const prevIdx = idx > 0 ? idx - 1 : null;
  const showPrevScene = prevIdx !== null && t < XFADE;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: "#000" }} data-testid="cinematic-demo-overlay">
      {/* Suppress native browser video controls */}
      <style dangerouslySetInnerHTML={{ __html: VIDEO_SUPPRESS_CSS }} />
      {/* Scene Area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Previous scene (fading out during crossfade) — render at 90% duration for stable visual */}
        {showPrevScene && (
          <div className="absolute inset-0" style={{ opacity: 1 - fadeInOpacity, zIndex: 1, willChange: "opacity" }}>
            <SceneRenderer scene={SCENES[prevIdx]} t={Math.min(SCENES[prevIdx].duration * 0.9, SCENES[prevIdx].duration - 800)} />
          </div>
        )}
        {/* Current scene (fading in) */}
        <div className="absolute inset-0" style={{
          opacity: fadeInOpacity,
          zIndex: 2,
          willChange: "opacity",
        }}>
          <SceneRenderer scene={scene} t={t} />
        </div>
      </div>

      {/* Controls Bar */}
      <div className="flex items-center gap-3 px-4 py-2" style={{ background: "rgba(0,0,0,0.85)", borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid="demo-controls">
        {/* Play/Pause */}
        <button onClick={togglePlay} className="p-1.5 rounded-md hover:bg-white/10 transition-colors" data-testid="demo-play-pause">
          {playing ? <Pause size={16} className="text-white/80" /> : <Play size={16} className="text-white/80" />}
        </button>
        {/* Restart */}
        <button onClick={restart} className="p-1.5 rounded-md hover:bg-white/10 transition-colors" data-testid="demo-restart">
          <RotateCcw size={14} className="text-white/50" />
        </button>
        {/* Time */}
        <span className="text-[11px] text-white/40 min-w-[70px]" style={{ fontFamily: FONT.mono }}>{timeStr}</span>
        {/* Mute */}
        <button onClick={() => setMuted(m => !m)} className="p-1.5 rounded-md hover:bg-white/10 transition-colors" data-testid="demo-mute">
          {muted ? <VolumeX size={14} className="text-white/50" /> : <Volume2 size={14} className="text-white/50" />}
        </button>

        {/* Progress Bar */}
        <div className="flex-1 mx-2 cursor-pointer" onClick={handleProgressClick} data-testid="demo-progress-bar">
          <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="absolute top-0 left-0 h-full rounded-full transition-[width] duration-100" style={{ width: `${progress * 100}%`, background: "linear-gradient(90deg, #10B981, #3B82F6)" }}/>
          </div>
          {/* Scene dots */}
          <div className="flex mt-1 gap-0.5">
            {SCENES.map((sc, i) => {
              const scStart = SCENES.slice(0, i).reduce((s, x) => s + x.duration, 0);
              const isCurrent = i === idx;
              return (
                <div key={sc.id} style={{
                  flex: sc.duration,
                  height: "3px", borderRadius: "1.5px",
                  background: isCurrent ? (sc.badgeColor || "#10B981") : elapsed >= scStart + sc.duration ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)",
                  transition: "background .3s ease",
                }}/>
              );
            })}
          </div>
        </div>

        {/* Close */}
        {onClose && (
          <button onClick={onClose} className="text-xs text-white/40 hover:text-white/70 transition-colors ml-2" data-testid="demo-close">
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default CinematicDemo;
